import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AdminGroup, useGetAdminGroupsQuery } from '../../api/group/groupAdminApiSlice';
import { useGetLocationListQuery } from '../../api/locations/locationsApiSlice';
import RowActionsMenu from '../../components/RowActionsMenu';
import TableCard, { TableHeaderRow, tableCellClass } from '../../components/TableCard';
import { getCurrentLang } from '../../i18n/i18n';
import { formatCurrency } from '../../utils';
import EditDepositModal from './components/EditDepositModal';
import { makeHasPermissionSelector } from '../../selector/authenticationSelector';
import Permission from '../../common/permissions';

/**
 * Per-group deposit configuration — the surface from
 * `docs/plans/deposit-admin-group-config.md`, unchanged except that it is now
 * ONE section of the Services area rather than the whole page. It lists groups,
 * read-only except the deposit amount; group CRUD stays out of scope.
 *
 * Still SUP-only, and still filtered on the group's own `compartmentTypes`
 * rather than on the tenant's service list: deposits are a property of the SUP
 * product groups, so a tenant that offers SUP among other things sees exactly
 * its SUP groups here. The section renders nothing at all when there are none,
 * so a non-SUP tenant does not get an empty table in the middle of their
 * configuration.
 */
const DepositsConfigSection = () => {
    const { t } = useTranslation();
    const { data: groups, isLoading, isError } = useGetAdminGroupsQuery();
    // Location names for the Location column (locationId -> name).
    const { data: locations } = useGetLocationListQuery(undefined);
    const [editingGroup, setEditingGroup] = useState<AdminGroup | null>(null);
    // Setting the deposit is for owner and manager, not service (PO,
    // 2026-10-29) — `edit:deposit-settings`, the string its PATCH checks.
    const canEdit = useSelector(makeHasPermissionSelector(Permission.editDepositSettings()));

    // Compare case-insensitively per the API-values rule.
    const supGroups = useMemo(
        () =>
            (groups ?? []).filter((group) =>
                group.compartmentTypes.some((type) => type.toLowerCase() === 'sup'),
            ),
        [groups],
    );

    const locationNameById = useMemo(
        () => new Map((locations ?? []).map((location) => [location.id, location.name])),
        [locations],
    );

    // Nothing to configure and nothing loading or broken — stay off the page
    // entirely rather than showing an empty table to a tenant with no SUP
    // groups. An error still renders, so a failure is never mistaken for
    // "there are none".
    if (!isLoading && !isError && supGroups.length === 0) return null;

    return (
        <section id={'deposits'} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                {t('services.deposits.title')}
            </h2>
            <p className={'text-sm text-foreground/60 mb-5'}>
                {t('services.deposits.description')}
            </p>

            {isLoading ? (
                <div className={'flex justify-center py-10'}>
                    <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
                </div>
            ) : isError ? (
                <p className={'text-sm text-foreground/60'}>
                    {t('services.deposits.load_error')}
                </p>
            ) : (
                <TableCard>
                    <TableHeaderRow
                        labels={[
                            t('services.deposits.columns.name'),
                            t('services.deposits.columns.location'),
                            t('services.deposits.columns.deposit'),
                            '',
                        ]}
                    />
                    <tbody>
                        {supGroups.map((group) => (
                            <tr key={group.id} className={'border-b border-foreground/5 last:border-b-0'}>
                                <td className={tableCellClass}>
                                    {group.name ?? (
                                        <span className={'italic text-foreground/40'}>
                                            {t('common.missing')}
                                        </span>
                                    )}
                                </td>
                                <td className={tableCellClass}>
                                    {(group.locationId &&
                                        locationNameById.get(group.locationId)) ?? (
                                        <span className={'italic text-foreground/40'}>
                                            {t('common.not_applicable')}
                                        </span>
                                    )}
                                </td>
                                <td className={tableCellClass}>
                                    {group.depositAmount != null
                                        ? formatCurrency(group.depositAmount, getCurrentLang())
                                        : t('services.deposits.no_deposit')}
                                </td>
                                <td className={`${tableCellClass} text-right`}>
                                    {/* Setting the deposit changes something and is not
                                        frequent, so it sits in the row's menu
                                        (design-decisions.md › Zeilenaktionen in Tabellen). */}
                                    <RowActionsMenu
                                        rowName={group.name || t('common.missing')}
                                        items={[{
                                            id: 'edit',
                                            label: t('common.edit'),
                                            onClick: () => setEditingGroup(group),
                                            hidden: !canEdit,
                                        }]}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </TableCard>
            )}

            {/* Mounted per group so the amount state initializes from the row. */}
            {editingGroup && (
                <EditDepositModal
                    group={editingGroup}
                    onClose={() => setEditingGroup(null)}
                />
            )}
        </section>
    );
};

export default DepositsConfigSection;
